import React, { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppScreen } from '../../components/AppScreen';
import { KioskFaceCapture } from '../../components/KioskFaceCapture';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { checkInVisitor, checkOutVisitor } from '../../services/visitorService';
import type { AttendanceCapture } from '../../types/faceAttendance';
import type { VisitorFaceResult } from '../../types/visitor';
import { formatDateTime } from '../../utils/dateTime';
import { deleteTemporaryImage } from '../../utils/temporaryImage';
import { visitorErrorMessage } from '../../utils/visitorErrors';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<
  RootStackParamList,
  'VisitorCheckIn' | 'VisitorCheckOut'
>;

type Phase = 'idle' | 'capturing' | 'submitting' | 'success' | 'error';

/**
 * Visitors never sign in: the signed-in front-desk user opens this screen and
 * the visitor presents their face. Check-in and check-out share the flow.
 */
export function VisitorFaceScanScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const isCheckIn = route.name === routes.visitorCheckIn;
  const title = isCheckIn ? 'Visitor check-in' : 'Visitor check-out';
  const [phase, setPhase] = useState<Phase>('idle');
  const [result, setResult] = useState<VisitorFaceResult | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const themed = useMemo(
    () => ({
      heading: { ...typography.screenTitle, color: palette.textPrimary, textAlign: 'center' as const },
      body: { ...typography.body, color: palette.textSecondary, textAlign: 'center' as const },
      welcome: {
        ...typography.screenTitle,
        color: palette.success,
        textAlign: 'center' as const,
      },
      error: { ...typography.body, color: palette.error, textAlign: 'center' as const },
      detail: { ...typography.body, color: palette.textPrimary },
    }),
    [palette],
  );

  const submitCapture = useCallback(
    async (capture: AttendanceCapture) => {
      setPhase('submitting');
      try {
        const image = {
          uri: capture.uri,
          fileName: capture.fileName || `visitor-${Date.now()}.jpg`,
          mimeType: capture.mimeType,
          source: 'CAMERA' as const,
        };
        const recorded = isCheckIn
          ? await checkInVisitor(image)
          : await checkOutVisitor(image);
        setResult(recorded);
        setMessage(null);
        setPhase('success');
      } catch (error) {
        setResult(null);
        setMessage(
          visitorErrorMessage(
            error,
            isCheckIn ? 'Unable to check the visitor in.' : 'Unable to check the visitor out.',
          ),
        );
        setPhase('error');
      } finally {
        deleteTemporaryImage(capture.uri).catch(() => undefined);
      }
    },
    [isCheckIn],
  );

  const onCaptureError = useCallback((captureMessage: string) => {
    setMessage(captureMessage || 'Unable to open the camera.');
    setPhase('error');
  }, []);

  const onTimeout = useCallback(() => {
    setMessage('No face was detected. Ask the visitor to face the camera and try again.');
    setPhase('error');
  }, []);

  const restart = useCallback(() => {
    setResult(null);
    setMessage(null);
    setPhase('capturing');
  }, []);

  if (phase === 'capturing') {
    return (
      <View style={styles.cameraHost}>
        <KioskFaceCapture
          heading={title}
          onCaptured={capture => {
            submitCapture(capture).catch(() => undefined);
          }}
          onError={onCaptureError}
          onTimeout={onTimeout}
        />
        <AppButton
          title="Cancel"
          variant="secondary"
          onPress={() => setPhase('idle')}
          style={styles.cancel}
        />
      </View>
    );
  }

  return (
    <PermissionGuard
      permission={PERMISSIONS.VISITOR_MANAGE}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen scroll>
        <ScreenHeader title={title} onBack={navigation.goBack} />
        <View style={styles.body}>
          {phase === 'idle' ? (
            <AppCard style={styles.card}>
              <Text style={themed.heading}>{title}</Text>
              <Text style={themed.body}>
                {isCheckIn
                  ? 'Ask the registered visitor to look at the camera. Their face is verified before the check-in time is recorded.'
                  : 'Ask the visitor to look at the camera. Their face is verified before the check-out time is recorded.'}
              </Text>
              <AppButton title="Scan visitor face" onPress={restart} />
            </AppCard>
          ) : null}

          {phase === 'submitting' ? (
            <AppCard style={styles.card}>
              <ActivityIndicator size="large" color={palette.primary} />
              <Text style={themed.body}>Verifying face…</Text>
            </AppCard>
          ) : null}

          {phase === 'success' && result ? (
            <AppCard style={styles.card}>
              <Text style={themed.welcome} accessibilityRole="header">
                {result.message}
              </Text>
              <View style={styles.details}>
                <Text style={themed.detail}>
                  {[result.visit.designation, result.visit.companyName]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
                <Text style={themed.detail}>
                  Checked in: {formatDateTime(result.visit.checkInAt)}
                </Text>
                {result.visit.checkOutAt ? (
                  <Text style={themed.detail}>
                    Checked out: {formatDateTime(result.visit.checkOutAt)}
                  </Text>
                ) : null}
              </View>
              <AppButton title="Next visitor" onPress={restart} />
              <AppButton title="Done" variant="secondary" onPress={navigation.goBack} />
            </AppCard>
          ) : null}

          {phase === 'error' ? (
            <AppCard style={styles.card}>
              <Text style={themed.error} accessibilityRole="alert">
                {message}
              </Text>
              <AppButton title="Try again" onPress={restart} />
              {isCheckIn ? (
                <AppButton
                  title="Register visitor"
                  variant="secondary"
                  onPress={() => navigation.navigate(routes.visitorRegister)}
                />
              ) : null}
            </AppCard>
          ) : null}
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  cameraHost: { flex: 1 },
  cancel: { margin: spacing.lg },
  body: { marginTop: spacing.lg },
  card: { gap: spacing.md, alignItems: 'stretch' },
  details: { gap: spacing.xs },
});
